import React from 'react';
import styled from 'styled-components';
import { Bio } from '../../data/constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.card} 0%, ${({ theme }) => theme.card_light} 100%);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 24px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(255, 107, 107, 0.2);
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #FF6B6B, #ff8a8a, #ffb3b3);
  }
  
  @keyframes slideUp {
    from { 
      transform: translateY(50px);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    max-width: 90%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 24px;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.3s ease;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 107, 107, 0.1);
    transform: scale(1.1);
  }
`;

const EmojiContainer = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  animation: bounce 2s ease-in-out infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 50%, #FF6B6B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  opacity: 0.9;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const GitHubButton = styled.a`
  background: linear-gradient(135deg, #FF6B6B, #ff8a8a);
  color: white;
  padding: 14px 28px;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(255, 107, 107, 0.4);
    background: linear-gradient(135deg, #ff8a8a, #FF6B6B);
    
    &::before {
      left: 100%;
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 15px;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.text_primary};
  padding: 14px 28px;
  border: 2px solid rgba(255, 107, 107, 0.3);
  border-radius: 16px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 107, 107, 0.1);
    border-color: #FF6B6B;
    color: #FF6B6B;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 15px;
  }
`;

const ProjectsComingSoon = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <EmojiContainer>🚀</EmojiContainer>
        
        <Title>Projects Coming Soon!</Title>
        
        <Subtitle>Something Epic is in the Works! 🔥</Subtitle>
        
        <Description>
          I'm crafting an amazing projects showcase that'll blow your mind! 
          In the meantime, check out my GitHub where all the magic happens - 
          tons of cool projects, clean code, and innovative solutions waiting for you! 💻✨
        </Description>
        
        <ButtonContainer>
          <GitHubButton href={Bio.github} target="_blank" rel="noopener noreferrer">
            🐙 Explore My GitHub
          </GitHubButton>
          <SecondaryButton onClick={onClose}>
            Got it! 👍
          </SecondaryButton>
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ProjectsComingSoon;
